/**
 * GuideManagement
 * Admin-sida för att hantera onboarding-guider (turar och steg).
 * Tillgänglig enbart för is_global_admin.
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PlayIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from '@heroicons/react/24/outline';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/solid';
import { useTranslation } from '@/store/languageStore';
import { PageHeader } from '@/components/common/PageHeader';
import Joyride from 'react-joyride';
import type { Step } from 'react-joyride';

// Ruttmappning: tour_key → sida där guiden ska köras
const TOUR_ROUTES: Record<string, string> = {
  module_fleet: '/fleet/overview',
  module_reports: '/reports/trips',
  module_kundtjanst: '/booking/bookings-search',
  module_kundtjanst_list: '/booking/bookings-search',
  module_kundtjanst_timeline: '/booking/bookings-search',
};

// ─── Types ───────────────────────────────────────────────────

interface GuideStep {
  id: number;
  tour_id: number;
  step_order: number;
  target_selector: string | null;
  title: string | null;
  content: string | null;
  placement: 'top' | 'bottom' | 'left' | 'right' | 'center';
  is_active: boolean;
}

interface GuideTour {
  id: number;
  tour_key: string;
  name: string;
  description: string | null;
  is_active: boolean;
  sort_order: number;
  steps: GuideStep[];
}

// ─── API helpers ──────────────────────────────────────────────

async function apiFetch<T>(url: string, options?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });
  const json = await res.json();
  if (!json.success) throw new Error(json.error || 'API error');
  return json.data as T;
}

// ─── Step Form ────────────────────────────────────────────────

interface StepFormData {
  title: string;
  content: string;
  target_selector: string;
  placement: GuideStep['placement'];
  step_order: number;
}

const EMPTY_STEP: StepFormData = {
  title: '',
  content: '',
  target_selector: '',
  placement: 'bottom',
  step_order: 0,
};

function StepForm({
  initial,
  onSave,
  onCancel,
}: {
  initial: StepFormData;
  onSave: (data: StepFormData) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<StepFormData>(initial);

  const set = (field: keyof StepFormData, value: string | number) =>
    setForm((f) => ({ ...f, [field]: value }));

  return (
    <div className="space-y-3 bg-bgBase p-4 rounded-lg border border-border">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-textSecondary mb-1">
            {t('guide.admin.stepTitle')}
          </label>
          <input
            value={form.title}
            onChange={(e) => set('title', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bgElevated text-textDefault"
            placeholder={t('guide.admin.stepTitlePlaceholder')}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-textSecondary mb-1">
            {t('guide.admin.stepOrder')}
          </label>
          <input
            type="number"
            value={form.step_order}
            onChange={(e) => set('step_order', parseInt(e.target.value) || 0)}
            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bgElevated text-textDefault"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-textSecondary mb-1">
          {t('guide.admin.content')}
        </label>
        <textarea
          rows={3}
          value={form.content}
          onChange={(e) => set('content', e.target.value)}
          className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bgElevated text-textDefault"
          placeholder={t('guide.admin.contentPlaceholder')}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-textSecondary mb-1">
            {t('guide.admin.targetSelector')}
            <span className="ml-1 text-textTertiary font-normal">
              ({t('guide.admin.targetSelectorHint')})
            </span>
          </label>
          <input
            value={form.target_selector}
            onChange={(e) => set('target_selector', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bgElevated text-textDefault font-mono"
            placeholder='[data-guide="element-name"]'
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-textSecondary mb-1">
            {t('guide.admin.placement')}
          </label>
          <select
            value={form.placement}
            onChange={(e) => set('placement', e.target.value as GuideStep['placement'])}
            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bgElevated text-textDefault"
          >
            <option value="top">top</option>
            <option value="bottom">bottom</option>
            <option value="left">left</option>
            <option value="right">right</option>
            <option value="center">center (modal)</option>
          </select>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <button
          onClick={onCancel}
          className="px-3 py-1.5 text-sm text-textSecondary hover:text-textDefault border border-border rounded-lg"
        >
          {t('common.cancel')}
        </button>
        <button
          onClick={() => onSave(form)}
          className="px-4 py-1.5 text-sm bg-bgPrimary text-white rounded-lg hover:bg-bgPrimaryHover"
        >
          {t('common.save')}
        </button>
      </div>
    </div>
  );
}

// ─── Tour Form ────────────────────────────────────────────────

function TourForm({
  initial,
  onSave,
  onCancel,
}: {
  initial: { tour_key: string; name: string; description: string; sort_order: number };
  onSave: (data: typeof initial) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState(initial);
  const set = (field: keyof typeof form, value: string | number) =>
    setForm((f) => ({ ...f, [field]: value }));

  return (
    <div className="space-y-3 bg-bgBase p-4 rounded-lg border border-border">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-textSecondary mb-1">
            {t('guide.admin.tourKey')} *
          </label>
          <input
            value={form.tour_key}
            onChange={(e) => set('tour_key', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bgElevated text-textDefault font-mono"
            placeholder="module_fleet"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-textSecondary mb-1">
            {t('guide.admin.tourName')} *
          </label>
          <input
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bgElevated text-textDefault"
            placeholder={t('guide.admin.tourNamePlaceholder')}
          />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <label className="block text-xs font-medium text-textSecondary mb-1">
            {t('guide.admin.tourDescription')}
          </label>
          <input
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bgElevated text-textDefault"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-textSecondary mb-1">
            {t('guide.admin.sortOrder')}
          </label>
          <input
            type="number"
            value={form.sort_order}
            onChange={(e) => set('sort_order', parseInt(e.target.value) || 0)}
            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bgElevated text-textDefault"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button
          onClick={onCancel}
          className="px-3 py-1.5 text-sm text-textSecondary hover:text-textDefault border border-border rounded-lg"
        >
          {t('common.cancel')}
        </button>
        <button
          onClick={() => onSave(form)}
          disabled={!form.tour_key || !form.name}
          className="px-4 py-1.5 text-sm bg-bgPrimary text-white rounded-lg hover:bg-bgPrimaryHover disabled:opacity-50"
        >
          {t('common.save')}
        </button>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────

export default function GuideManagement() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tours, setTours] = useState<GuideTour[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedTours, setExpandedTours] = useState<Set<number>>(new Set());
  const [showNewTourForm, setShowNewTourForm] = useState(false);
  const [editingTourId, setEditingTourId] = useState<number | null>(null);
  const [editingStepId, setEditingStepId] = useState<number | null>(null);
  const [addingStepToTourId, setAddingStepToTourId] = useState<number | null>(null);

  // Förhandsvisning
  const [previewSteps, setPreviewSteps] = useState<Step[]>([]);
  const [previewRun, setPreviewRun] = useState(false);

  const loadTours = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiFetch<GuideTour[]>('/api/guides/tours?includeInactive=true');
      setTours(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useState(() => { loadTours(); });

  const toggleExpand = (id: number) => {
    setExpandedTours((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ── Create tour ────────────────────────────────────────────

  const handleCreateTour = async (form: {
    tour_key: string;
    name: string;
    description: string;
    sort_order: number;
  }) => {
    try {
      await apiFetch('/api/guides/tours', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      setShowNewTourForm(false);
      await loadTours();
    } catch (e: any) {
      setError(e.message);
    }
  };

  // ── Update tour ────────────────────────────────────────────

  const handleUpdateTour = async (
    id: number,
    form: { tour_key: string; name: string; description: string; sort_order: number }
  ) => {
    try {
      await apiFetch(`/api/guides/tours/${id}`, {
        method: 'PUT',
        body: JSON.stringify(form),
      });
      setEditingTourId(null);
      await loadTours();
    } catch (e: any) {
      setError(e.message);
    }
  };

  // ── Toggle tour active ─────────────────────────────────────

  const handleToggleTourActive = async (tour: GuideTour) => {
    try {
      await apiFetch(`/api/guides/tours/${tour.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: !tour.is_active }),
      });
      await loadTours();
    } catch (e: any) {
      setError(e.message);
    }
  };

  // ── Delete tour ────────────────────────────────────────────

  const handleDeleteTour = async (id: number) => {
    if (!confirm(t('guide.admin.confirmDeleteTour'))) return;
    try {
      await apiFetch(`/api/guides/tours/${id}`, { method: 'DELETE' });
      await loadTours();
    } catch (e: any) {
      setError(e.message);
    }
  };

  // ── Create step ────────────────────────────────────────────

  const handleCreateStep = async (tourId: number, form: StepFormData) => {
    try {
      await apiFetch(`/api/guides/tours/${tourId}/steps`, {
        method: 'POST',
        body: JSON.stringify({
          ...form,
          target_selector: form.target_selector || null,
        }),
      });
      setAddingStepToTourId(null);
      await loadTours();
    } catch (e: any) {
      setError(e.message);
    }
  };

  // ── Update step ────────────────────────────────────────────

  const handleUpdateStep = async (stepId: number, form: StepFormData) => {
    try {
      await apiFetch(`/api/guides/steps/${stepId}`, {
        method: 'PUT',
        body: JSON.stringify({
          ...form,
          target_selector: form.target_selector || null,
        }),
      });
      setEditingStepId(null);
      await loadTours();
    } catch (e: any) {
      setError(e.message);
    }
  };

  // ── Delete step ────────────────────────────────────────────

  const handleDeleteStep = async (stepId: number) => {
    if (!confirm(t('guide.admin.confirmDeleteStep'))) return;
    try {
      await apiFetch(`/api/guides/steps/${stepId}`, { method: 'DELETE' });
      await loadTours();
    } catch (e: any) {
      setError(e.message);
    }
  };

  // ── Move step ──────────────────────────────────────────────

  const handleMoveStep = async (tour: GuideTour, stepId: number, direction: 'up' | 'down') => {
    const sorted = [...tour.steps].sort((a, b) => a.step_order - b.step_order);
    const idx = sorted.findIndex((s) => s.id === stepId);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;

    const reordered = sorted.map((s, i) => {
      if (i === idx) return { id: s.id, step_order: sorted[swapIdx].step_order };
      if (i === swapIdx) return { id: s.id, step_order: sorted[idx].step_order };
      return { id: s.id, step_order: s.step_order };
    });

    try {
      await apiFetch('/api/guides/steps/reorder', {
        method: 'PUT',
        body: JSON.stringify({ steps: reordered }),
      });
      await loadTours();
    } catch (e: any) {
      setError(e.message);
    }
  };

  // ── Preview ────────────────────────────────────────────────
  // Om turen har en känd modul-rutt navigerar vi dit och startar guiden
  // via location.state. Annars körs en lokal preview på admin-sidan
  // (sidebar-element som nav, user-menu finns alltid).

  const handlePreview = (tour: GuideTour) => {
    const moduleRoute = TOUR_ROUTES[tour.tour_key];

    if (moduleRoute) {
      // Navigera till modulens sida – sidan läser state och startar turen
      navigate(moduleRoute, { state: { startGuide: tour.tour_key } });
      return;
    }

    // Lokal preview (t.ex. välkomstturen – sidebar-element finns på alla sidor)
    const mapped: Step[] = [...tour.steps]
      .filter((s) => s.is_active)
      .sort((a, b) => a.step_order - b.step_order)
      .map((s) => {
        const elementExists = s.target_selector
          ? !!document.querySelector(s.target_selector)
          : false;
        const useRealTarget = !s.target_selector || elementExists;

        return {
          target: useRealTarget ? (s.target_selector ?? 'body') : 'body',
          title: s.title ?? undefined,
          content: useRealTarget
            ? (s.content ?? '')
            : `${s.content ?? ''}\n\n*${t('copy.admin.guideElementMissing', { selector: s.target_selector })}*`,
          placement: useRealTarget
            ? (s.target_selector ? s.placement : 'center')
            : ('center' as const),
          disableBeacon: useRealTarget ? !s.target_selector : true,
        };
      });

    setPreviewSteps(mapped);
    setPreviewRun(true);
  };

  // ─── Render ────────────────────────────────────────────────

  return (
    <div className="p-6">
      <PageHeader
        title={t('guide.admin.title')}
        description={t('guide.admin.description')}
        icon={<QuestionMarkCircleIcon className="w-6 h-6 text-primary" />}
        hideBack={false}
      />

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* New tour button */}
      <div className="flex justify-end mb-4">
        <button
          onClick={() => setShowNewTourForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-bgPrimary text-white rounded-lg hover:bg-bgPrimaryHover text-sm"
        >
          <PlusIcon className="w-4 h-4" />
          {t('guide.admin.newTour')}
        </button>
      </div>

      {/* New tour form */}
      {showNewTourForm && (
        <div className="mb-4">
          <TourForm
            initial={{ tour_key: '', name: '', description: '', sort_order: 0 }}
            onSave={handleCreateTour}
            onCancel={() => setShowNewTourForm(false)}
          />
        </div>
      )}

      {/* Tours list */}
      {loading ? (
        <p className="text-textSecondary text-sm">{t('common.loading')}</p>
      ) : tours.length === 0 ? (
        <p className="text-textSecondary text-sm">{t('guide.admin.noTours')}</p>
      ) : (
        <div className="space-y-3">
          {tours.map((tour) => {
            const isExpanded = expandedTours.has(tour.id);
            const isEditingTour = editingTourId === tour.id;
            const activeSteps = tour.steps.filter((s) => s.is_active);

            return (
              <div
                key={tour.id}
                className={`border rounded-xl overflow-hidden ${
                  tour.is_active ? 'border-border' : 'border-border opacity-60'
                }`}
              >
                {/* Tour header */}
                <div className="flex items-center gap-3 p-4 bg-bgElevated">
                  <button
                    onClick={() => toggleExpand(tour.id)}
                    className="text-textSecondary hover:text-textDefault"
                  >
                    {isExpanded ? (
                      <ChevronDownIcon className="w-4 h-4" />
                    ) : (
                      <ChevronRightIcon className="w-4 h-4" />
                    )}
                  </button>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-textDefault">{tour.name}</span>
                      <span className="text-xs font-mono text-textTertiary bg-bgBase px-2 py-0.5 rounded">
                        {tour.tour_key}
                      </span>
                      {!tour.is_active && (
                        <span className="text-xs text-textTertiary bg-bgBase px-2 py-0.5 rounded">
                          {t('guide.admin.inactive')}
                        </span>
                      )}
                    </div>
                    {tour.description && (
                      <p className="text-xs text-textSecondary mt-0.5 truncate">{tour.description}</p>
                    )}
                  </div>

                  <span className="text-sm text-textSecondary">
                    {activeSteps.length} {t('guide.admin.steps')}
                  </span>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handlePreview(tour)}
                      title={t('guide.admin.preview')}
                      className="p-1.5 rounded text-textSecondary hover:text-textDefault hover:bg-bgBase"
                    >
                      <PlayIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setEditingTourId(tour.id)}
                      title={t('common.edit')}
                      className="p-1.5 rounded text-textSecondary hover:text-textDefault hover:bg-bgBase"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleToggleTourActive(tour)}
                      title={tour.is_active ? t('guide.admin.deactivate') : t('guide.admin.activate')}
                      className="p-1.5 rounded text-textSecondary hover:text-textDefault hover:bg-bgBase text-xs"
                    >
                      {tour.is_active ? '●' : '○'}
                    </button>
                    <button
                      onClick={() => handleDeleteTour(tour.id)}
                      title={t('common.delete')}
                      className="p-1.5 rounded text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Edit tour form */}
                {isEditingTour && (
                  <div className="p-4 border-t border-border">
                    <TourForm
                      initial={{
                        tour_key: tour.tour_key,
                        name: tour.name,
                        description: tour.description ?? '',
                        sort_order: tour.sort_order,
                      }}
                      onSave={(form) => handleUpdateTour(tour.id, form)}
                      onCancel={() => setEditingTourId(null)}
                    />
                  </div>
                )}

                {/* Steps */}
                {isExpanded && (
                  <div className="border-t border-border">
                    {tour.steps.length === 0 ? (
                      <p className="p-4 text-sm text-textSecondary">
                        {t('guide.admin.noSteps')}
                      </p>
                    ) : (
                      <div className="divide-y divide-border">
                        {[...tour.steps]
                          .sort((a, b) => a.step_order - b.step_order)
                          .map((step, idx, arr) => (
                            <div key={step.id} className="p-4">
                              {editingStepId === step.id ? (
                                <StepForm
                                  initial={{
                                    title: step.title ?? '',
                                    content: step.content ?? '',
                                    target_selector: step.target_selector ?? '',
                                    placement: step.placement,
                                    step_order: step.step_order,
                                  }}
                                  onSave={(form) => handleUpdateStep(step.id, form)}
                                  onCancel={() => setEditingStepId(null)}
                                />
                              ) : (
                                <div className="flex items-start gap-3">
                                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-bgPrimary text-white text-xs flex items-center justify-center font-bold mt-0.5">
                                    {step.step_order}
                                  </span>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm text-textDefault">
                                      {step.title || <em className="text-textTertiary">{t('guide.admin.noTitle')}</em>}
                                    </p>
                                    <p className="text-xs text-textSecondary mt-0.5 line-clamp-2">
                                      {step.content}
                                    </p>
                                    {step.target_selector && (
                                      <code className="text-xs text-textTertiary bg-bgBase px-1.5 py-0.5 rounded mt-1 inline-block">
                                        {step.target_selector}
                                      </code>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    <button
                                      onClick={() => handleMoveStep(tour, step.id, 'up')}
                                      disabled={idx === 0}
                                      className="p-1 text-textTertiary hover:text-textDefault disabled:opacity-30"
                                    >
                                      <ArrowUpIcon className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      onClick={() => handleMoveStep(tour, step.id, 'down')}
                                      disabled={idx === arr.length - 1}
                                      className="p-1 text-textTertiary hover:text-textDefault disabled:opacity-30"
                                    >
                                      <ArrowDownIcon className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      onClick={() => setEditingStepId(step.id)}
                                      className="p-1 text-textSecondary hover:text-textDefault"
                                    >
                                      <PencilIcon className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteStep(step.id)}
                                      className="p-1 text-red-400 hover:text-red-600"
                                    >
                                      <TrashIcon className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                      </div>
                    )}

                    {/* Add step */}
                    <div className="p-4 border-t border-border bg-bgBase/50">
                      {addingStepToTourId === tour.id ? (
                        <StepForm
                          initial={{
                            ...EMPTY_STEP,
                            step_order: tour.steps.length + 1,
                          }}
                          onSave={(form) => handleCreateStep(tour.id, form)}
                          onCancel={() => setAddingStepToTourId(null)}
                        />
                      ) : (
                        <button
                          onClick={() => setAddingStepToTourId(tour.id)}
                          className="flex items-center gap-2 text-sm text-textSecondary hover:text-textDefault"
                        >
                          <PlusIcon className="w-4 h-4" />
                          {t('guide.admin.addStep')}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Preview Joyride */}
      {previewSteps.length > 0 && (
        <Joyride
          run={previewRun}
          steps={previewSteps}
          continuous
          showProgress
          showSkipButton
          callback={({ status }) => {
            if (status === 'finished' || status === 'skipped') {
              setPreviewRun(false);
              setPreviewSteps([]);
            }
          }}
          styles={{
            options: { primaryColor: 'var(--bgPrimary, #2563eb)', zIndex: 10000 },
            tooltip: { borderRadius: '0.75rem' },
          }}
        />
      )}
    </div>
  );
}
